import React, { useEffect, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/unused-import
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import {
  BarChart,
  Bar,
  XAxis,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
} from 'recharts';
import styled from 'styled-components';
import { Card } from './Card';
import { formatNumber } from '@/src/utils/helpers';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const P = styled.p`
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.2rem;
  padding: 1rem 1rem 1rem 0rem;
`;

const StyledChartWrapper = styled.div`
  width: 100%;
  padding: 2rem 1rem;
  @media screen and (max-width: 900px) {
    padding: 0rem;
  }
`;

type Payload = {
  name: string;
  label: string;
  value: number;
  fill: string;
};
interface StockBarChartProps {
  activeData: Payload[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shareHoldingData: Array<any>;
}

export const StockVolumeBarChart: React.FC<StockBarChartProps> = ({
  activeData,
  shareHoldingData,
}) => {
  const getXAxisFontSize = () => {
    if (window.innerWidth < 900) {
      return 10;
    } else {
      return 16;
    }
  };

  const maxValue = Math.max(
    ...Object.values(shareHoldingData).map((v) => v.totalVolume || 1),
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const calculatePercentage = (
    deliveryVolumeNormalized: number,
    totalVolumeNormalized: number,
  ): string => {
    if (totalVolumeNormalized === 0) return '0%';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return `${((deliveryVolumeNormalized / totalVolumeNormalized) * 100).toFixed(2)}%`;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomXAxisTick = ({ x, y, payload }: any) => {
    const data = activeDataSet.find((d) => d.xAxis === payload.value);
    return (
      <text
        x={x}
        y={y}
        textAnchor="middle"
        fontSize={labelFontSize}
        fontFamily="neueMontreal-Regular"
      >
        <tspan x={x} dy="1em">
          {payload.value}
        </tspan>
        {data && (
          <tspan x={x} dy="1.2em">
            {data.percentage}
          </tspan>
        )}
      </text>
    );
  };

  const activeDataSet = Object.entries(shareHoldingData).map(([key, value]) => {
    const deliveryVolume = value.deliveryVolume || 0;
    const totalVolume = value.totalVolume || 0;

    return {
      xAxis: key,
      deliveryVolume: formatNumber(deliveryVolume),
      totalVolume: formatNumber(totalVolume),
      totalVolumeNormalized: maxValue ? (totalVolume / maxValue) * 100 : 0,
      deliveryVolumeNormalized: maxValue
        ? (deliveryVolume / maxValue) * 100
        : 0,
      deliveryPercentage: calculatePercentage(deliveryVolume, totalVolume),
      percentage: calculatePercentage(deliveryVolume, totalVolume),
    };
  });

  const [labelFontSize, setLabelFontSize] = useState<string>('12px');
  const updateFontSize = () => {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 767) {
      setLabelFontSize('12px');
    } else {
      setLabelFontSize('16px');
    }
  };

  useEffect(() => {
    updateFontSize();
    window.addEventListener('resize', updateFontSize);
    return () => window.removeEventListener('resize', updateFontSize);
  }, []);

  return (
    <Card title={'Total Revenue & Profit'}>
      <StyledChartWrapper>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={activeDataSet} barSize={50}>
            <XAxis
              dataKey="xAxis"
              tick={<CustomXAxisTick />}
              fontSize={getXAxisFontSize()}
              axisLine={false}
              tickLine={false}
              fontFamily="neueMontreal-Regular"
              fontWeight={500}
              color="#ABABAB"
            />
            <CartesianGrid vertical={false} stroke="#e0e0e0" />
            <Bar
              dataKey="deliveryVolumeNormalized"
              fill={activeData[0].fill}
              style={{ outline: 'none' }}
              radius={[5, 5, 0, 0]}
            >
              <LabelList
                dataKey="deliveryVolume"
                position="top"
                style={{ fontSize: labelFontSize }}
              />
            </Bar>
            <Bar
              dataKey="totalVolumeNormalized"
              fill={activeData[1].fill}
              style={{ outline: 'none' }}
              radius={[5, 5, 0, 0]}
            >
              <LabelList
                dataKey="totalVolume"
                position="top"
                style={{ fontSize: labelFontSize }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </StyledChartWrapper>
    </Card>
  );
};
